import { useMemo } from "react";
import { Box } from "@mui/system";

import { useStoreState } from "store/store";
import { Addon, AddonCart } from "store/types";

import { AddonSummaryItem } from "./AddonSummaryItem";

type Props = {
  addonsToDisplay: AddonCart[];
};

const normalizeAddonPrice = (
  addon: AddonCart,
  resolvedPrice: number | string | undefined
) => {
  if (typeof resolvedPrice === "number" && Number.isFinite(resolvedPrice)) {
    return resolvedPrice;
  }

  if (typeof resolvedPrice === "string") {
    const parsed = parseFloat(resolvedPrice);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  if (typeof addon.price === "number" && Number.isFinite(addon.price)) {
    return addon.price;
  }

  return 0;
};

export const SummaryAddons = ({ addonsToDisplay }: Props) => {
  const currentCarAddons = useStoreState((state) => state.currentCarAddons);

  const resolvedAddons = useMemo(() => {
    if (!addonsToDisplay.length) {
      return addonsToDisplay;
    }

    const addonLookup = currentCarAddons.reduce<Record<string, Addon>>(
      (acc, addon) => {
        acc[String(addon.addon_id)] = addon;
        return acc;
      },
      {}
    );

    return addonsToDisplay.map((addon) => {
      const match = addonLookup[String(addon.addon_id)];
      if (!match) return addon;

      const resolvedPrice =
        addon.type?.toLowerCase() === "departure"
          ? match.round_trip_price
          : match.price;

      return {
        ...addon,
        price: normalizeAddonPrice(addon, resolvedPrice),
        name: match.name ?? addon.name,
      };
    });
  }, [addonsToDisplay, currentCarAddons]);

  const addonsToDisplayArrival = resolvedAddons.filter((el) => {
    const type = el.type?.toLowerCase();
    return type === "arrival";
  });
  const addonsToDisplayDeparture = resolvedAddons.filter((el) => {
    const type = el.type?.toLowerCase();
    return type === "departure";
  });
  const addonsCommon = resolvedAddons.filter((el) => {
    const type = el.type?.toLowerCase();
    return type !== "arrival" && type !== "departure";
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        marginTop: "24px",
      }}
    >
      {addonsCommon.length ? (
        <AddonSummaryItem addonsToDisplay={addonsCommon} />
      ) : null}
      {addonsToDisplayArrival.length ? (
        <AddonSummaryItem
          addonsToDisplay={addonsToDisplayArrival}
          label="Arrival"
        />
      ) : null}
      {addonsToDisplayDeparture.length ? (
        <AddonSummaryItem
          addonsToDisplay={addonsToDisplayDeparture}
          label="Departure"
        />
      ) : null}
    </Box>
  );
};
